import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { HeadingStyled } from "../../styled/Elements";
import ProfileApi from "../../../services/api/profileApi";
import { toast } from "react-toastify";
import FormSkeleton from "../../profile/FormSkeleton";
import DataSkeleton from "../../profile/DataSkeleton";
import { formatDate, getFormattedTime } from "../../../utils";

const InquiryModal = ({ show, onHide, id }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isArabic = lang === "ar";

  const [inquiryDetails, setInquiryDetails] = useState({});
  const [loading, setLoading] = useState(false);

  const getInquiryDetails = async (id) => {
    try {
      setLoading(true);
      const { data, message, code } = await ProfileApi.fetchInquiryDetails(id);
      if (code === 200 && data) {
        setInquiryDetails(data);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
      toast.error(t("errorFetchingData"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && show) {
      getInquiryDetails(id);
    }
  }, [id, show]);

  const {
    inquiry_type,
    property_type,
    property_category,
    price_range,
    customer_type,
    full_name,
    phone_number,
    cities_districts_data,
    bedrooms,
    inside_mall_only_bit,
    created_at,
    message,
  } = inquiryDetails || {};

  const formatValue = (value) => {
    if (!value || value === "" || value === null || value === undefined) {
      return "-";
    }
    return value;
  };

  const formatArrayValue = (array) => {
    if (!array || !array.length) return "-";
    const formattedBeds = array?.map((bed) =>
      bed === "10" ? "9+" : bed === "11" ? t("Studio") : bed
    );
    return formattedBeds.join(", ");
  };

  const formatPropertyType = (types) => {
    if (!types || !types.length) return "-";
    return types.map((type) => t(type || "unknown")).join(", ");
  };

  const formatBooleanValue = (value) => {
    if (value === "1" || value === 1 || value === true) return t("yes");
    if (value === "0" || value === 0 || value === false || !value) return t("no");
    return "-";
  };

  return (
    <StyledModal show={show} size="lg" onHide={onHide} centered>
      <ModalWrapper>
        <Modal.Header closeButton className="px-0">
          <HeadingStyled size="24px">{t("modalTitle")}</HeadingStyled>
        </Modal.Header>
        <Modal.Body className="px-0">
          {loading ? (
            <DataSkeleton />
          ) : (
            <>
              <InquiryGrid>
                <InquiryRow>
                  <Label>{t("searchFor")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(t(inquiry_type || "unknown"))}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("Property_category")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(property_category)}
                  </Value>
                </InquiryRow>
                <InquiryRow>
                  <Label>{t("propertyType")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatPropertyType(property_type)}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("bedroom")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatArrayValue(bedrooms)}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("priceRange")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(price_range)}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("Inside_Mall_Only")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {property_category === t("Residential")
                      ? "N/A"
                      : formatBooleanValue(inside_mall_only_bit)}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("clientType")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(customer_type ? t(customer_type) : "-")}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("fullName")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(full_name)}
                  </Value>
                </InquiryRow>

                <InquiryRow>
                  <Label>{t("phone")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {formatValue(phone_number)}
                  </Value>
                </InquiryRow>
                <InquiryRow>
                  <Label>{t("dateTime")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>
                    {`${formatDate(created_at)}  ${getFormattedTime(
                      created_at
                    )}`}
                  </Value>
                </InquiryRow>

                {/* <InquiryRow>
                <Label>{t("city")}</Label>
                <Value className={isArabic ? "arabic" : ""}>
                  {formatValue(cities_districts_data?.[0]?.name)}
                </Value>
              </InquiryRow>

              <InquiryRow>
                <Label>{t("districts")}</Label>
                <Value className={isArabic ? "arabic" : ""}>
                  {formatValue(t("-"))}
                </Value>
              </InquiryRow> */}
                {cities_districts_data?.length > 0 ? (
                  cities_districts_data.map((city, index) => (
                    <React.Fragment key={index}>
                      <InquiryRow>
                        <Label>{t("city")}</Label>
                        <Value className={isArabic ? "arabic" : ""}>
                          {formatValue(city.name)}
                        </Value>
                      </InquiryRow>
                      <InquiryRow>
                        <Label>{t("districts")}</Label>
                        <Value className={isArabic ? "arabic" : ""}>
                          {city.districts?.length > 0
                            ? city.districts.map((d) => d.name).join(", ")
                            : "-"}
                        </Value>
                      </InquiryRow>
                    </React.Fragment>
                  ))
                ) : (
                  <InquiryRow>
                    <Label>{t("city")}</Label>
                    <Value className={isArabic ? "arabic" : ""}>-</Value>
                  </InquiryRow>
                )}
              </InquiryGrid>
              {message && message !== "-" && (
                <InquiryRow className="message-row mt-4">
                  <Label>{t("message")}</Label>
                  <Value className={isArabic ? "arabic" : ""}>{message}</Value>
                </InquiryRow>
              )}
            </>
          )}
        </Modal.Body>
      </ModalWrapper>
    </StyledModal>
  );
};

const ModalWrapper = styled.div`
  margin-left: 30px;
  margin-right: 30px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  .btn-close {
    border: 1px solid var(--text3);
    border-radius: 50%;

    :hover {
      border-color: var(--text3);
    }
  }
  @media (max-width: 576px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

export default InquiryModal;

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 800px;
    margin: 1.75rem auto;
  }

  .modal-content {
    border: none;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 24px 32px 20px;
  }

  .modal-body {
    padding: 24px 32px 32px;
  }

  .btn-close {
    font-size: 1.2rem;
    opacity: 0.6;

    &:hover {
      opacity: 0.8;
    }
  }

  @media (max-width: 576px) {
    .modal-dialog {
      margin: 0.5rem;
      max-width: calc(100% - 1rem);
    }

    .modal-header {
      padding: 16px;
    }

    .modal-body {
      padding: 16px;
    }

    .modal-header h1,
    .modal-header h2,
    .modal-header h3,
    .modal-header h4,
    .modal-header h5,
    .modal-header h6,
    .modal-header p,
    .modal-header div {
      font-size: 18px !important;
      word-break: break-word;
      overflow-wrap: anywhere;
      min-width: 0;
      padding-inline-end: 8px;
    }
  }
`;

const InquiryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 40px;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InquiryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px 12px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #8b8b8b;
  line-height: 1.4;
  min-width: 0;
  flex: 1 1 auto;
  text-align: start;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const Value = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  line-height: 1.4;
  min-height: 21px;
  min-width: 0;
  flex: 0 1 auto;
  max-width: 100%;
  text-align: end;
  overflow-wrap: anywhere;
  word-break: break-word;

  &.arabic {
    font-weight: 600;
  }

  @media (max-width: 576px) {
    font-size: 15px;
    text-align: start;
  }
`;
